/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR319+CR380: S2 Web Scan Debug Panel — shows URL lineage, flags, cookie remediation
 * JS results, screenshot links, and provides "Copy Debug Bundle" for fast diagnosis.
 *
 * CR380: Enhanced to display Firecrawl v2 artifacts: javascriptReturns, screenshots,
 * endpoint version, and status code.
 */
import { useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Copy, ChevronDown, ExternalLink, AlertTriangle, CheckCircle2, Globe, Search, Image, Code } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface S2WebScanDebugPanelProps {
  batchRunId: string;
  parentPocRunId?: string | null;
}

interface ReadStepRow {
  id: string;
  merchant_id: string;
  resolve_source_url_used: string | null;
  resolve_notes: string | null;
  text_char_count: number | null;
  resolve_extraction_quality_score: number | null;
  error_message: string | null;
  status: string;
  duration_ms: number | null;
}

interface ParsedDiagnostics {
  merchantId: string;
  configuredUrl: string;
  requestedUrl: string;
  finalUrl: string | null;
  statusCode: number | null;
  firecrawlEndpoint: string | null;
  flags: string[];
  qualityScore: number | null;
  textLength: number | null;
  cookieWallDetected: boolean;
  recipeAttempts: any[];
  recipeUsed: any;
  failureReason: string | null;
  status: string;
  durationMs: number | null;
  // CR380: Rich Firecrawl artifacts
  javascriptReturns: any[] | null;
  screenshotUrls: string[] | null;
  raw: any;
}

export function S2WebScanDebugPanel({ batchRunId, parentPocRunId }: S2WebScanDebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState<"all" | "blocked" | "ok">("all");

  // Fetch READ steps for this batch run
  const { data: readSteps, isLoading } = useQuery({
    queryKey: ["s2-debug-panel", batchRunId, parentPocRunId],
    queryFn: async () => {
      if (!parentPocRunId) return [];
      const { data, error } = await supabase
        .from("poc_run_steps")
        .select("id, merchant_id, resolve_source_url_used, resolve_notes, text_char_count, resolve_extraction_quality_score, error_message, status, duration_ms")
        .eq("poc_run_id", parentPocRunId)
        .eq("step_phase", "READ")
        .order("merchant_id", { ascending: true });
      if (error) throw error;
      return (data || []) as ReadStepRow[];
    },
    enabled: !!parentPocRunId && isOpen,
    staleTime: 30_000,
  });

  // Parse diagnostics from resolve_notes
  const diagnostics: ParsedDiagnostics[] = useMemo(() => {
    if (!readSteps) return [];
    return readSteps.map((step) => {
      // CR382: Handle resolve_notes as jsonb (object) or string
      const notes: any =
        typeof step.resolve_notes === "string"
          ? (() => { try { return JSON.parse(step.resolve_notes); } catch { return {}; } })()
          : (step.resolve_notes ?? {});

      const recipeUsed = notes.recipe_used || null;
      const recipeAttempts = notes.recipe_attempts || [];
      const cookieWallDetected = notes.cookie_wall_detected || false;
      const firecrawlDiag = notes.firecrawl || {};
      const urlLineage = notes.url_lineage || {};

      // Extract flags from notes or infer from error
      const flags: string[] = [];
      if (step.error_message) {
        if (step.error_message.includes("COOKIE")) flags.push("COOKIE_TEXT_DOMINANT");
        if (step.error_message.includes("LOCATION_GATE")) flags.push("LOCATION_GATE_PRESENT");
        if (step.error_message.includes("TIMEOUT")) flags.push("NETWORK_TIMEOUT");
        if (step.error_message.includes("BOT")) flags.push("CAPTCHA_OR_BOT_BLOCK");
        if (flags.length === 0) flags.push(step.error_message);
      }
      if (cookieWallDetected) flags.push("COOKIE_WALL_DETECTED");

      // CR382: Use url_lineage for distinct configured/requested/final URLs
      const lastAttempt = recipeAttempts.length > 0 ? recipeAttempts[recipeAttempts.length - 1] : null;
      const configuredUrl = urlLineage.configured_url || step.resolve_source_url_used || "—";
      const requestedUrl = urlLineage.requested_url || step.resolve_source_url_used || "—";
      const finalUrl = urlLineage.final_url || firecrawlDiag.final_url || lastAttempt?.final_url || null;
      const statusCode = firecrawlDiag.status_code || lastAttempt?.status_code || null;
      const firecrawlEndpoint = firecrawlDiag.endpoint || lastAttempt?.firecrawl_endpoint || null;
      const javascriptReturns = firecrawlDiag.javascript_returns || lastAttempt?.javascript_returns || null;
      const screenshotUrls = firecrawlDiag.screenshots || lastAttempt?.screenshot_urls || null;

      return {
        merchantId: step.merchant_id,
        configuredUrl,
        requestedUrl,
        finalUrl,
        statusCode,
        firecrawlEndpoint,
        flags,
        qualityScore: step.resolve_extraction_quality_score,
        textLength: step.text_char_count,
        cookieWallDetected,
        recipeAttempts,
        recipeUsed,
        failureReason: step.error_message,
        status: step.status,
        durationMs: step.duration_ms,
        javascriptReturns,
        screenshotUrls,
        raw: notes,
      };
    });
  }, [readSteps]);

  // Filtered diagnostics
  const filtered = useMemo(() => {
    let items = diagnostics;
    if (filterMode === "blocked") {
      items = items.filter(d => d.flags.length > 0 || d.status === "failed");
    } else if (filterMode === "ok") {
      items = items.filter(d => d.status === "success");
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(d =>
        d.merchantId.toLowerCase().includes(lower) ||
        d.configuredUrl.toLowerCase().includes(lower)
      );
    }
    return items;
  }, [diagnostics, filterMode, searchTerm]);

  // Summary stats
  const summary = useMemo(() => {
    const total = diagnostics.length;
    const success = diagnostics.filter(d => d.status === "success").length;
    const failed = diagnostics.filter(d => d.status === "failed").length;
    const cookieBlocked = diagnostics.filter(d => d.cookieWallDetected).length;
    const locationGated = diagnostics.filter(d => d.flags.some(f => f.includes("LOCATION_GATE"))).length;
    return { total, success, failed, cookieBlocked, locationGated };
  }, [diagnostics]);

  // Build debug bundle for clipboard — CR380: includes rich artifacts
  const handleCopyDebugBundle = useCallback(() => {
    const bundle = {
      cr380_debug_bundle: true,
      batch_run_id: batchRunId,
      parent_poc_run_id: parentPocRunId,
      generated_at: new Date().toISOString(),
      summary,
      merchants: diagnostics.map(d => ({
        merchant_id: d.merchantId,
        configured_url: d.configuredUrl,
        requested_url: d.requestedUrl,
        final_url: d.finalUrl,
        status_code: d.statusCode,
        firecrawl_endpoint: d.firecrawlEndpoint,
        status: d.status,
        quality_score: d.qualityScore,
        text_length: d.textLength,
        flags: d.flags,
        cookie_wall_detected: d.cookieWallDetected,
        failure_reason: d.failureReason,
        duration_ms: d.durationMs,
        recipe_used: d.recipeUsed,
        recipe_attempts: d.recipeAttempts,
        // CR380: Rich artifacts
        javascript_returns: d.javascriptReturns ? JSON.stringify(d.javascriptReturns).substring(0, 500) : null,
        screenshot_urls: d.screenshotUrls?.slice(0, 2) || null,
      })),
    };
    navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
    toast.success("Debug bundle copied to clipboard");
  }, [batchRunId, parentPocRunId, summary, diagnostics]);

  if (!parentPocRunId) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-3 bg-muted/30 rounded-lg border text-left hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2 text-sm">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Web Scan Debug Panel</span>
            {diagnostics.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {summary.success}/{summary.total} OK
                {summary.cookieBlocked > 0 && (
                  <span className="text-yellow-600 ml-1">· {summary.cookieBlocked} cookie-blocked</span>
                )}
                {summary.locationGated > 0 && (
                  <span className="text-orange-600 ml-1">· {summary.locationGated} location-gated</span>
                )}
              </span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 space-y-3">
          {/* Controls row */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search merchant or URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <div className="flex gap-1">
              {(["all", "blocked", "ok"] as const).map((mode) => (
                <Button
                  key={mode}
                  variant={filterMode === mode ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilterMode(mode)}
                >
                  {mode === "all" ? `All (${summary.total})` :
                   mode === "blocked" ? `Blocked (${summary.failed})` :
                   `OK (${summary.success})`}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={handleCopyDebugBundle}
            >
              <Copy className="h-3 w-3" />
              Copy Debug Bundle
            </Button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-sm text-muted-foreground p-4 text-center">Loading scan diagnostics…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 text-center">
              {diagnostics.length === 0 ? "No READ steps found for this batch." : "No matches for current filter."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left p-2 font-medium">Merchant</th>
                    <th className="text-left p-2 font-medium">Source URL</th>
                    <th className="text-left p-2 font-medium">Status</th>
                    <th className="text-left p-2 font-medium">Flags</th>
                    <th className="text-right p-2 font-medium">Quality</th>
                    <th className="text-right p-2 font-medium">Chars</th>
                    <th className="text-left p-2 font-medium">Recipe</th>
                    <th className="text-right p-2 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d, idx) => (
                    <MerchantDebugRow key={d.merchantId + idx} diag={d} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function MerchantDebugRow({ diag }: { diag: ParsedDiagnostics }) {
  const [expanded, setExpanded] = useState(false);
  const isOk = diag.status === "success";

  return (
    <>
      <tr
        className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="p-2 font-medium whitespace-nowrap">{diag.merchantId}</td>
        <td className="p-2 max-w-[200px]">
          <div className="flex items-center gap-1">
            <span className="truncate" title={diag.configuredUrl}>
              {diag.configuredUrl}
            </span>
            {diag.configuredUrl !== "—" && (
              <button
                className="shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(diag.configuredUrl);
                  toast.success("URL copied");
                }}
              >
                <Copy className="h-3 w-3" />
              </button>
            )}
          </div>
        </td>
        <td className="p-2">
          {isOk ? (
            <Badge variant="default" className="text-xs bg-green-600/90 hover:bg-green-600">
              <CheckCircle2 className="h-3 w-3 mr-0.5" /> OK
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-xs">
              <AlertTriangle className="h-3 w-3 mr-0.5" /> {diag.failureReason?.replace("FAILED_", "") || "FAILED"}
            </Badge>
          )}
        </td>
        <td className="p-2">
          <div className="flex gap-1 flex-wrap">
            {diag.flags.slice(0, 3).map((flag, i) => (
              <Badge key={i} variant="outline" className="text-xs whitespace-nowrap">
                {flag.replace(/_/g, " ").toLowerCase()}
              </Badge>
            ))}
            {diag.flags.length === 0 && <span className="text-muted-foreground">—</span>}
          </div>
        </td>
        <td className="p-2 text-right font-mono">
          {diag.qualityScore != null ? diag.qualityScore.toFixed(2) : "—"}
        </td>
        <td className="p-2 text-right font-mono">
          {diag.textLength != null ? diag.textLength.toLocaleString() : "—"}
        </td>
        <td className="p-2">
          {diag.recipeUsed ? (
            <span className="text-xs" title={diag.recipeUsed.recipe_used_name || "—"}>
              {diag.recipeUsed.recipe_used_source === "OVERRIDE" ? (
                <Badge variant="secondary" className="text-xs">Override</Badge>
              ) : diag.recipeUsed.recipe_used_source === "S0_DEFAULT" ? (
                <Badge variant="outline" className="text-xs">S0</Badge>
              ) : (
                <span className="text-muted-foreground">Heuristic</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="p-2 text-right font-mono whitespace-nowrap">
          {diag.durationMs != null ? `${(diag.durationMs / 1000).toFixed(1)}s` : "—"}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="p-3 bg-muted/20 border-b">
            <MerchantDebugDetails diag={diag} />
          </td>
        </tr>
      )}
    </>
  );
}

function MerchantDebugDetails({ diag }: { diag: ParsedDiagnostics }) {
  return (
    <div className="space-y-3 text-xs">
      {/* URL lineage + endpoint info */}
      <div className="space-y-1">
        <div className="font-medium text-muted-foreground uppercase tracking-wide">URL Lineage</div>
        <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
          <span className="text-muted-foreground">Configured:</span>
          <UrlDisplay url={diag.configuredUrl} />
          <span className="text-muted-foreground">Requested:</span>
          <UrlDisplay url={diag.requestedUrl} />
          {diag.finalUrl && (
            <>
              <span className="text-muted-foreground">Final (redirect):</span>
              <UrlDisplay url={diag.finalUrl} />
            </>
          )}
          {diag.firecrawlEndpoint && (
            <>
              <span className="text-muted-foreground">Endpoint:</span>
              <span className="font-mono">{diag.firecrawlEndpoint}</span>
            </>
          )}
          {diag.statusCode != null && (
            <>
              <span className="text-muted-foreground">Status Code:</span>
              <span className="font-mono">
                <Badge variant={diag.statusCode >= 200 && diag.statusCode < 400 ? "default" : "destructive"} className="text-xs">
                  {diag.statusCode}
                </Badge>
              </span>
            </>
          )}
        </div>
      </div>

      {/* CR380: Cookie remediation JS results */}
      {diag.javascriptReturns && diag.javascriptReturns.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Code className="h-3 w-3" />
            Cookie Remediation Results
          </div>
          <div className="space-y-1">
            {diag.javascriptReturns.map((jsReturn: any, i: number) => {
              const result = typeof jsReturn === 'string' ? (() => { try { return JSON.parse(jsReturn); } catch { return jsReturn; } })() : jsReturn;
              return (
                <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-muted/40 font-mono text-xs">
                  {result?.cookieClicked !== undefined && (
                    <Badge variant={result.cookieClicked ? "default" : "outline"} className="text-xs">
                      {result.cookieClicked ? `✓ Clicked: "${result.label || '?'}"` : "No cookie button found"}
                    </Badge>
                  )}
                  {result?.overlaysRemoved !== undefined && (
                    <Badge variant={result.overlaysRemoved > 0 ? "default" : "outline"} className="text-xs">
                      {result.overlaysRemoved > 0 ? `${result.overlaysRemoved} overlays removed` : "No overlays found"}
                    </Badge>
                  )}
                  {result?.cookieClicked === undefined && result?.overlaysRemoved === undefined && (
                    <span className="text-muted-foreground">{JSON.stringify(result).substring(0, 100)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CR380: Screenshot links */}
      {diag.screenshotUrls && diag.screenshotUrls.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
            <Image className="h-3 w-3" />
            Screenshots ({diag.screenshotUrls.length})
          </div>
          <div className="flex gap-2 flex-wrap">
            {diag.screenshotUrls.slice(0, 3).map((url: string, i: number) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <Image className="h-3 w-3" /> Screenshot {i + 1}
                <ExternalLink className="h-3 w-3" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Recipe attempts */}
      {diag.recipeAttempts.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium text-muted-foreground uppercase tracking-wide">Recipe Attempts ({diag.recipeAttempts.length})</div>
          <div className="space-y-1">
            {diag.recipeAttempts.map((ra: any, i: number) => (
              <div key={i} className={`flex items-center gap-2 p-1.5 rounded ${ra.detected_cookie_wall ? "bg-yellow-500/10" : "bg-green-500/10"}`}>
                <Badge variant="outline" className="text-xs shrink-0">#{ra.attempt}</Badge>
                <span className="font-medium">{ra.recipe_name || ra.recipe_source}</span>
                <span className="text-muted-foreground">
                  {ra.text_length} chars · q={ra.quality_score?.toFixed(2)}
                </span>
                {ra.detected_cookie_wall && (
                  <Badge variant="destructive" className="text-xs">cookie wall</Badge>
                )}
                {!ra.detected_cookie_wall && ra.text_length > 0 && (
                  <Badge variant="default" className="text-xs bg-green-600/90">clear</Badge>
                )}
                {ra.status_code && (
                  <Badge variant="outline" className="text-xs font-mono">{ra.status_code}</Badge>
                )}
                {ra.duration_ms > 0 && (
                  <span className="text-muted-foreground ml-auto">{(ra.duration_ms / 1000).toFixed(1)}s</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failure reason */}
      {diag.failureReason && (
        <div className="p-2 bg-destructive/10 text-destructive rounded">
          <span className="font-medium">Blocked:</span> {diag.failureReason}
        </div>
      )}

      {/* Copy single merchant debug */}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 text-xs gap-1"
        onClick={() => {
          navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
          toast.success("Merchant debug copied");
        }}
      >
        <Copy className="h-3 w-3" />
        Copy this merchant's debug
      </Button>
    </div>
  );
}

function UrlDisplay({ url }: { url: string }) {
  if (!url || url === "—") return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-1 min-w-0">
      <span className="font-mono truncate" title={url}>{url}</span>
      <button
        className="shrink-0 text-muted-foreground hover:text-foreground"
        onClick={() => {
          navigator.clipboard.writeText(url);
          toast.success("URL copied");
        }}
      >
        <Copy className="h-3 w-3" />
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 text-muted-foreground hover:text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}